import { useAppSelector } from "@app/hooks";
import { LTooltip } from "@components/LTooltip";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavItems } from "@helpers/navigation";
import { OrganizationStatus } from "@helpers/organizationHelper";
import { AvailableAssessment } from "@store/generalTypes";
import { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router";
import { LBtn } from "../../../../../common/components/LBtn";
import { LProgressLine } from "../../../../../common/components/LProgressLine";
import { Colors } from "../../../../../styles/colors";
import { useLocalStorage, LocalStorageKey } from "@helpers/localStorageHelper";
import { AssessmentBasket } from "../../../../purchase/pages/checkout";

const SHOW_PURCHASE_ASSESSMENTS_LINK = false;

export const AccountPlans = (): JSX.Element => {
    const [isLoading, setIsLoading] = useState(true);
    const [availableAssessment, setAvailableAssessment] = useState<AvailableAssessment>();
    const [progressLineColor, setProgressLineColor] = useState<{ strokeColor: string; trailColor: string }>();
    const [, setAssessmentBasket] = useLocalStorage<AssessmentBasket>(LocalStorageKey.AssessmentBasket);

    const { organization, organizationAssessments } = useAppSelector((x) => {
        return { organization: x.organization, organizationAssessments: x.organizationAssessments };
    });

    const isActive = organization.status === OrganizationStatus.active.toLocaleLowerCase();

    const navigate = useNavigate();

    useEffect(() => {
        setIsLoading(true);
        void loadAvailablePurchases();
    }, [organizationAssessments]);

    const loadAvailablePurchases = useCallback(async () => {
        const strokeColor = organizationAssessments.warning ? Colors.errorRed : Colors.primaryBlue;
        const trailColor = organizationAssessments.warning ? Colors.errorRedBg : Colors.lightestBlue;

        setProgressLineColor({ strokeColor, trailColor });

        setAvailableAssessment(organizationAssessments);
        setIsLoading(false);
    }, [organization, organizationAssessments]);

    const handleOnPurchaseClick = (): void => {
        setAssessmentBasket({ organizationId: organization.id!, quantity: 0 });
        navigate(NavItems.Purchase.Pricing.path);
    };

    if (organizationAssessments.loading || isLoading) {
        return (
            <>
                <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center mt-6">
                    <h1 className="is-size-5 has-text-ldark-gray">Account Plan</h1>
                </div>
                <div className="divider" />
                <div>
                    <div className="is-flex is-flex-direction-row is-align-items-center">
                        <div className="column is-one-quarter pb-1">
                            <Skeleton />
                        </div>
                    </div>
                    <div className="is-flex is-flex-direction-row is-align-items-center">
                        <div className="column is-two-thirds pb-1">
                            <Skeleton />
                        </div>
                    </div>
                    <div className="is-flex is-flex-direction-row is-align-items-center">
                        <div className="column is-1 pb-1">
                            <Skeleton />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center mt-6">
                <h1 className="is-size-5 has-text-ldark-gray">Account Plan</h1>
                {SHOW_PURCHASE_ASSESSMENTS_LINK && (
                    <LBtn variation="unstyled" onClick={handleOnPurchaseClick}>
                        <FontAwesomeIcon className="px-1" icon={solid("cart-shopping")} />
                        <p className="has-text-weight-semibold">Purchase additional assessments</p>
                    </LBtn>
                )}
            </div>
            <div className="divider" />

            <div className="columns">
                <div className="column is-two-thirds">
                    <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center">
                        <div className="has-text-weight-bold">
                            <span>Plan Usage </span>
                            {!isActive && (
                                <LTooltip
                                    text={
                                        "We are still processing the order. You will receive an email confirmation once the order is processed. You may need to manually refresh the page to see your purchased assessments shown right away"
                                    }
                                />
                            )}
                        </div>
                        <div className="is-flex is-flex-direction-row">
                            <p className="has-text-weight-bold mr-1">{availableAssessment?.usedAssessments}</p>
                            <p className="has-text-grey">{`of ${availableAssessment?.purchases} assessments used`}</p>
                        </div>
                    </div>
                    <div className="my-4">
                        <LProgressLine
                            percent={availableAssessment!.availableAssessmentPercentage}
                            strokeWidth={1.75}
                            strokeColor={progressLineColor!.strokeColor}
                            trailColor={progressLineColor!.trailColor}
                        />
                    </div>
                    <div className="label is-small has-text-accent-dolphin has-text-weight-light">
                        {`${availableAssessment!.availableAssessments} assessments remaining`}
                    </div>
                </div>
            </div>
        </>
    );
};
